import moment, { Moment } from 'moment';
import { LocalizedDateTimeFormats } from '~/utils/datetime/localized-date-time-formats';
import { StringUtils } from '~/utils/string-utils';

/**
 * NOTE(Jake): 2018-09-06
 * - 'L' default case, we just want the date.
 * - 'L LT' is a special case.
 * -- Used by: ato-interfaces.html, removal of this logic wont be caught by typecheckers.
 */
type DateFormatView =
	| LocalizedDateTimeFormats.Date
	| LocalizedDateTimeFormats.DateShort
	| LocalizedDateTimeFormats.DateTime
	| LocalizedDateTimeFormats.DateTimeWithSeconds;

type TimeFormatView =
	| LocalizedDateTimeFormats.Time
	| LocalizedDateTimeFormats.TimeWithSeconds;

/**
 * Formats use tokens based on Moment.js library: https://momentjs.com/docs/#/displaying/format/
 */
export class DateFormatValueConverter {
	public static readonly apiFormat = 'YYYY-MM-DDTHH:mm:ss';
	private static readonly apiDateFormat = 'YYYY-MM-DD';
	private static readonly apiTimeFormat = 'HH:mm:ss';
	private static readonly displayFormats = [
		LocalizedDateTimeFormats.Date,
		LocalizedDateTimeFormats.DateShort,
		DateFormatValueConverter.apiDateFormat,
		DateFormatValueConverter.apiFormat,
		DateFormatValueConverter.apiTimeFormat,
	];
	private static readonly apiFormats = [
		DateFormatValueConverter.apiFormat,
		DateFormatValueConverter.apiDateFormat,
	];

	// null database values for dates are represented by these moments
	private static readonly nullDate = moment.utc(
		'0001-01-01',
		DateFormatValueConverter.apiDateFormat,
	);

	/**
	 * This method will automatically run on forms with this converter attached.
	 *  @param {string} value Original date or timestamp value
	 * @param {string} format Optional
	 * @returns {string} Converted date or timestamp value
	 */
	public toView(value: string, format?: DateFormatView): string {
		if (!value) {
			return '';
		}
		return DateFormatValueConverter.toViewStatic(value, format);
	}

	public fromView(value: string): string {
		return DateFormatValueConverter.fromViewStatic(value);
	}

	/**
	 * Strings that have a valid API format are converted to strings in Localised
	 * date format or strings that match format provided.
	 * If string value matches a null database value, an empty string is returned
	 * If string does not have a valid API format, unaltered string is returned
	 * @param {string} value Original date or timestamp value
	 * @param {DateFormatView} format Optional
	 * @returns {string} Converted date or timestamp value
	 */
	public static toViewStatic(
		value: string,
		format?: DateFormatView | TimeFormatView,
		keepLocalTime?: boolean,
	): string {
		let conValue = value;
		if (DateFormatValueConverter.isValidApiFormat(value)) {
			// fix for difference to how Tree View and Browser View set offset to null timestamps
			let nullTimestamp = moment(
				'1900-01-01T00:00:00',
				DateFormatValueConverter.apiFormat,
			);
			if (
				moment(
					'1900-01-01T00:00:00',
					DateFormatValueConverter.apiFormat,
				).isDST()
			) {
				nullTimestamp = moment(
					'1900-01-01T00:00:00',
					DateFormatValueConverter.apiFormat,
				).add(1, 'h');
			}
			if (
				moment
					.utc(value, DateFormatValueConverter.apiFormat)
					.isSame(nullTimestamp) ||
				moment
					.utc(value, DateFormatValueConverter.apiDateFormat)
					.isSame(DateFormatValueConverter.nullDate)
			) {
				return '';
			}
			// NOTE(Jake): 2018-09-05 - WEBUI-961
			// If you modify this logic, please ensure dates are converting correctly
			// for alternate languages/locales, ie. Australia vs United States.
			conValue = moment
				.utc(value, DateFormatValueConverter.apiFormats)
				.local(keepLocalTime)
				.format(format ? format : LocalizedDateTimeFormats.Date);
		}
		return conValue;
	}

	public static fromViewStatic(value: string): string {
		let conValue = value;
		if (DateFormatValueConverter.isValidDisplayFormat(value)) {
			// NOTE(Jake): 2018-09-05
			// moment() requires a different format to `InputMaskLocaleAliases`
			// eg. en-AU: DD/MM/YYYY
			// eg. en-US: MM/DD/YYYY
			const momentLocaleFormat = moment.localeData().longDateFormat('L');
			// NOTE(Jake): 2018-09-05 - WEBUI-961
			// If you modify this logic, please ensure dates are converting correctly
			// for alternate languages/locales, ie. Australia vs United States.
			conValue = moment(value, momentLocaleFormat).format(
				DateFormatValueConverter.apiFormat,
			);
		}
		return conValue;
	}

	private static isValidDisplayFormat(value: string): boolean {
		return moment(
			value,
			DateFormatValueConverter.displayFormats,
			true,
		).isValid();
	}

	public static isValidApiFormat(value: string): boolean {
		return moment(
			value,
			DateFormatValueConverter.apiFormats,
			true,
		).isValid();
	}

	/**
	 * Convert the date time value to Moment
	 * @param value date time value in string
	 * @return Moment|null
	 */
	public static toMoment(value: string): Moment | null {
		if (parseInt(value, 10) <= -36000000) {
			return null;
		} else if (value && String(value).trim().length !== 0) {
			const incomingFormats = [
				'x',
				LocalizedDateTimeFormats.Date,
				LocalizedDateTimeFormats.DateTime,
				LocalizedDateTimeFormats.DateTimeWithSeconds,
				DateFormatValueConverter.apiDateFormat,
				DateFormatValueConverter.apiFormat,
				DateFormatValueConverter.apiTimeFormat,
			];
			if (moment(value, incomingFormats, true).isValid()) {
				return moment(value, incomingFormats);
			}
		}
		return null;
	}

	/**
	 * Format the date time string into the given format;
	 * @param value date time value in string
	 * @param format new format
	 * @return string
	 */
	public static toDate(value, format): string {
		const dateMoment = DateFormatValueConverter.toMoment(value);
		if (dateMoment) {
			return dateMoment.format(format);
		} else {
			return '';
		}
	}

	public static toLocaleDate(value: string): string {
		return DateFormatValueConverter.toDate(
			value,
			LocalizedDateTimeFormats.Date,
		);
	}

	/**
	 * Convert moment locale format to flatpickr locale format
	 * See https://flatpickr.js.org/formatting/#date-formatting-tokens for details
	 * - en-AU: DD/MM/YYYY becomes d/m/Y
	 * - en-US: MM/DD/YYYY becomes m/d/Y
	 */
	public static toFlatpickrLocaleDateFormat(): string {
		const localeData = moment.localeData();
		let format = localeData.longDateFormat(LocalizedDateTimeFormats.Date);
		format = format.replace('DD', 'd');
		format = format.replace('MM', 'm');
		format = format.replace('YYYY', 'Y');
		return format;
	}

	/**
	 * Convert moment locale format to flatpickr locale format and with time
	 *
	 * See https://flatpickr.js.org/formatting/#time-formatting-tokens for details
	 *
	 * The date time and date time with seconds options are introduced in WEBUI-4704.
	 *
	 * Date time format `01/25/2023 03:33 PM` have been used in the WebUI widely,
	 * so it is the default format for date and time, e.g.: it could be applied to the modified date.
	 *
	 * Date time with seconds format `01/25/2023 21:33:00` is required for `api-audit` form,
	 * which may contain a huge amount of records in a second.
	 * Also, using 24 hours format is to make it easy for user to chose the time and
	 * is to save the filter space when `BETWEEN` filter is selected.
	 * Please confirm with the team before applying this format.
	 *
	 * @param enableTime flag to enable hours and minutes
	 * @param enableSeconds flag to enable hours, minutes and seconds
	 * @returns
	 */
	public static toFlatpickrLocaleDateTimeFormat(
		enableTime: boolean,
		enableSeconds: boolean,
	): string {
		let format = DateFormatValueConverter.toFlatpickrLocaleDateFormat();
		if (enableTime) {
			const timeFormat = enableSeconds ? 'H:i:S' : 'G:i K';
			format += ' ' + timeFormat;
		}
		return format;
	}

	/**
	 * Generate current locale date format that recognizable by momentjs
	 * e.g. for locale of en-au, will return DD/MM/YYYY
	 * if time and second is enabled, return DD/MM/YYYY HH:mm:ss
	 * @param enableTime
	 * @param enableSeconds
	 * @returns
	 */
	public static toLocaleDateFormat(
		enableTime?: boolean,
		enableSeconds?: boolean,
	): string {
		let format = moment
			.localeData()
			.longDateFormat(LocalizedDateTimeFormats.Date);
		if (enableTime) {
			const timeFormat = enableSeconds ? 'HH:mm:ss' : 'HH:mm';
			format += ' ' + timeFormat;
		}
		return format;
	}

	public static toTime(value) {
		if (
			value.trim().length !== 0 &&
			moment(value, [
				'hh:mm:ss',
				DateFormatValueConverter.apiFormat,
			]).isValid()
		) {
			return moment(value, [
				'hh:mm:ss',
				DateFormatValueConverter.apiFormat,
			]).format('HH:mm');
		} else {
			return '';
		}
	}

	public static toPeriodTime(value) {
		if (
			value.trim().length !== 0 &&
			moment(value, [
				'hh:mm:ss',
				DateFormatValueConverter.apiFormat,
			]).isValid()
		) {
			return moment(value, [
				'hh:mm:ss',
				DateFormatValueConverter.apiFormat,
			]).format('h:mmA');
		} else {
			return '';
		}
	}

	public static toAcurityApiFormat(value) {
		return DateFormatValueConverter.toDate(
			value,
			DateFormatValueConverter.apiFormat,
		);
	}

	public static getApiFormat(value: Date): string {
		return moment(value).format(DateFormatValueConverter.apiFormat);
	}

	public static toTimestamp(value) {
		return DateFormatValueConverter.toDate(value, 'x');
	}

	public static millisecondsToDuration(duration: number) {
		// Need to use StringUtils.padStart() instead of String().padStart()
		// because ECMAScript 2017 not currently supported in TreeView
		const milliseconds = StringUtils.padStart(
			String(duration % 1000),
			3,
			'0',
		);
		const seconds = StringUtils.padStart(
			String(Math.floor((duration / 1000) % 60)),
			2,
			'0',
		);
		const minutes = StringUtils.padStart(
			String(Math.floor((duration / (1000 * 60)) % 60)),
			2,
			'0',
		);
		const hours = StringUtils.padStart(
			String(Math.floor(duration / (1000 * 60 * 60))),
			2,
			'0',
		);

		return hours + ':' + minutes + ':' + seconds + '.' + milliseconds;
	}
}
